import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "-");

export default function DeliveryOrder() {
  const { doId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = user && (user.role === "OWNER" || user.role === "ADMIN_INVENTORY");
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/inventory/delivery-orders/${doId}`).then(({ data }) => setDoc(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [doId]);
  useEffect(() => { load(); }, [load]);

  const printDo = () => {
    api.post(`/inventory/delivery-orders/${doId}/mark-printed`).catch(() => {});
    window.print();
  };

  if (user && !canView) {
    return <div><AppTopBar active="sales" /><main className="page"><p className="hint">Akses ditolak.</p></main></div>;
  }

  return (
    <div>
      <AppTopBar active="sales" />
      <main className="page" style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="page-head no-print">
          <button className="btn-ghost" onClick={() => navigate("/inventory/manual-sales")} data-testid="do-back-button">← Kembali</button>
          {doc && <button className="btn-primary" onClick={printDo} data-testid="do-print-button">Cetak Surat Jalan (A4)</button>}
        </div>
        {error && <div className="alert-error no-print" data-testid="do-error">{error}</div>}

        {doc && (
          <section className="panel print-area" data-testid="do-print-area">
            <div className="do-header">
              <div>
                <div className="do-title">SURAT JALAN</div>
                <div className="do-subtitle">Delivery Order</div>
              </div>
              <div className="do-meta">
                <div><strong>No:</strong> <span data-testid="do-number">{doc.delivery_order_number}</span></div>
                <div><strong>Tanggal:</strong> {fmtDate(doc.delivery_date)}</div>
                <div><strong>Ref Penjualan:</strong> {doc.sale_number}</div>
                {doc.reference && <div><strong>Referensi:</strong> {doc.reference}</div>}
              </div>
            </div>

            <div className="do-parties">
              <div className="do-party">
                <div className="do-party-label">PENGIRIM</div>
                <div className="do-party-name">{doc.business_name || "-"}</div>
                {doc.business_address && <div>{doc.business_address}</div>}
              </div>
              <div className="do-party">
                <div className="do-party-label">PENERIMA</div>
                <div className="do-party-name" data-testid="do-recipient">{doc.recipient_name || "UMUM"}</div>
                {doc.recipient_phone && <div>Telp/WA: {doc.recipient_phone}</div>}
                {doc.shipping_address && <div data-testid="do-shipping">{doc.shipping_address}</div>}
                {doc.shipping_city && <div>{doc.shipping_city}</div>}
              </div>
            </div>

            <table className="do-table" data-testid="do-items-table">
              <thead>
                <tr><th style={{ width: 40 }}>No</th><th>SKU</th><th>Deskripsi</th><th style={{ width: 70 }}>Qty</th><th style={{ width: 90 }}>Satuan</th><th>Konversi</th></tr>
              </thead>
              <tbody>
                {doc.items.map((it, i) => (
                  <tr key={i} data-testid={`do-item-${it.sku_code}`}>
                    <td>{i + 1}</td>
                    <td className="mono">{it.sku_code}</td>
                    <td>{it.description}</td>
                    <td className="mono">{it.uom_qty}</td>
                    <td className="mono">{it.uom_code}</td>
                    <td>{it.conversion_to_base_snapshot > 1 ? `1 ${it.uom_code} = ${it.conversion_to_base_snapshot} ${it.base_uom} · Total ${it.base_qty} ${it.base_uom}` : `${it.base_qty} ${it.base_uom}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {doc.note && <div className="do-note"><strong>Catatan:</strong> {doc.note}</div>}

            <div className="do-signatures">
              {["Disiapkan / Gudang", "Dikirim Oleh", "Diterima Oleh"].map((label) => (
                <div className="do-sign" key={label}>
                  <div className="do-sign-label">{label}</div>
                  <div className="do-sign-space" />
                  <div className="do-sign-line">Nama: __________________</div>
                  <div className="do-sign-line">Tanggal: ________________</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
